import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { supabase } from '../../supabaseClient'
import {
  J, ReflectionBlock, ViewToggle, AiEngagement, FeedbackRounds, handlePrint, fmtDate, fmtDateTime,
  StepReflection, JournalNote, UsageRow, FeedbackRow,
} from '../../components/journal/paper'
import StepReflectionCard from '../../components/journal/StepReflectionCard'

// Step 4 research journal — narrative Journal view (hypothesis + operationalized
// variables) and a Documentation (audit) view (mentor review, AI feedback
// rounds, AI engagement + ISEF disclosure). Built on the shared paper kit.

interface Block {
  dv?: string; dv_kind?: 'direct' | 'derived'; dv_measurement?: string
  measured_quantities?: string; formula?: string
  needs_tool?: boolean; tool?: string
  hypothesis?: string; rationale?: string; supporting_background?: string
}
interface EngReq { requirement?: string; criterion?: string }
interface Payload {
  question?: string; iv?: string; cv?: string; blocks?: Block[]
  // engineering track
  problem?: string; for_human_user?: boolean; users_needs?: string; performance_goal?: string
  prior_art?: string; requirements?: EngReq[]; mvp?: string; tools?: string; solution?: string
  // legacy pre-block keys
  iv_type?: string; trials?: string; dv?: string; dv_measurement?: string
  needs_apparatus?: boolean; apparatus?: string; hypothesis?: string; justification?: string
}
interface ReviewEntry { action: string; note: string | null; acted_at: string }

const AI_LABELS: Record<string, string> = { 'step4-evaluate-field': 'Hypothesis field feedback' }

// Fold new (block) or legacy (single-field) payloads into a common render shape.
function normalize(p: Payload): { question?: string; iv?: string; cv?: string; blocks: Block[] } {
  if (Array.isArray(p.blocks)) return { question: p.question, iv: p.iv, cv: p.cv, blocks: p.blocks }
  const iv = [p.iv, p.trials].map(s => (s ?? '').trim()).filter(Boolean).join('\n\n')
  return {
    question: p.question, iv, cv: p.cv ?? '',
    blocks: [{
      dv: p.dv, dv_measurement: p.dv_measurement,
      needs_tool: p.needs_apparatus, tool: p.apparatus,
      hypothesis: p.hypothesis, rationale: p.justification,
    }],
  }
}

// AI-feedback-round labels: groups may carry a `:index` block suffix, and old
// entries use legacy field names — resolve both to a friendly label.
const GROUP_LABEL: Record<string, string> = {
  question: 'Research question', iv: 'Independent variable', cv: 'Controlled variables',
  dv: 'Dependent variable', tool: 'Measurement tool', hypothesis: 'Hypothesis & rationale',
  iv_type: 'Variable type', trials: 'Trial setup', dv_measurement: 'Measurement',
  apparatus: 'Measurement tool', justification: 'Rationale',
  // engineering
  problem: 'Problem', audience: 'Users & needs', prior_art: 'Prior art & gaps',
  requirement: 'Design requirement', solution: 'Solution / MVP',
}
function labelForField(field: string): string {
  const [g, idx] = field.split(':')
  const base = GROUP_LABEL[g] ?? g
  if (idx === undefined) return base
  const unit = g === 'requirement' ? 'requirement' : 'hypothesis'
  return `${base} (${unit} ${Number(idx) + 1})`
}

export default function JournalTab({ projectId, studentId }: { projectId: string; studentId: string }) {
  const [payload,    setPayload]    = useState<Payload | null>(null)
  const [status,     setStatus]     = useState<string>('draft')
  const [mentorNote, setMentorNote] = useState<string | null>(null)
  const [topic,      setTopic]      = useState<string | null>(null)
  const [notes,      setNotes]      = useState<JournalNote[]>([])
  const [reflection, setReflection] = useState<StepReflection | null>(null)
  const [reviewLog,  setReviewLog]  = useState<ReviewEntry[]>([])
  const [usage,      setUsage]      = useState<UsageRow[]>([])
  const [feedback,   setFeedback]   = useState<FeedbackRow[]>([])
  const [updatedAt,  setUpdatedAt]  = useState<string | null>(null)
  const [track,      setTrack]      = useState<string>('scientific_method')
  const [loading,    setLoading]    = useState(true)
  const [view,       setView]       = useState<'journal' | 'documentation'>('journal')

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [{ data: hd }, { data: summary }, { data: noteRows }, { data: refl }, { data: log }, { data: usageRows }, { data: fbRows }] = await Promise.all([
        supabase.from('hypothesis_design').select('payload, status, mentor_note, updated_at, track').eq('project_id', projectId).maybeSingle(),
        supabase.from('step1_summary').select('confirmed_topic').eq('project_id', projectId).maybeSingle(),
        supabase.from('journal_note').select('note_id, step_number, source_label, content, saved_at').eq('project_id', projectId).eq('step_number', 4).order('saved_at'),
        supabase.from('step_reflection').select('*').eq('project_id', projectId).eq('step_number', 4).maybeSingle(),
        supabase.from('mentor_review_log').select('action, note, acted_at').eq('project_id', projectId).in('action', ['comment', 'approve']).order('acted_at'),
        supabase.from('ai_usage_log').select('function_name, model, input_tokens, output_tokens').eq('project_id', projectId).like('function_name', 'step4%'),
        supabase.from('ai_feedback_log').select('field, evaluation, created_at').eq('project_id', projectId).eq('step_number', 4).order('created_at'),
      ])
      if (cancelled) return
      setPayload(((hd as any)?.payload ?? {}) as Payload)
      setStatus(((hd as any)?.status ?? 'draft'))
      setMentorNote(((hd as any)?.mentor_note ?? null))
      setUpdatedAt((hd as any)?.updated_at ?? null)
      setTrack(((hd as any)?.track ?? 'scientific_method'))
      setTopic((summary as any)?.confirmed_topic ?? null)
      setNotes((noteRows as JournalNote[] | null) ?? [])
      setReflection((refl as StepReflection | null) ?? null)
      setReviewLog((log as ReviewEntry[] | null) ?? [])
      setUsage((usageRows as UsageRow[] | null) ?? [])
      setFeedback((fbRows as FeedbackRow[] | null) ?? [])
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [projectId, studentId])

  async function reloadReflection() {
    const { data: refl } = await supabase.from('step_reflection').select('*')
      .eq('project_id', projectId).eq('step_number', 4).maybeSingle()
    setReflection((refl as StepReflection | null) ?? null)
  }

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>

  const p        = payload ?? {}
  const isEng    = track === 'engineering_design'
  const n        = normalize(payload ?? {})
  const dateStr  = updatedAt ? fmtDate(updatedAt) : fmtDate(new Date().toISOString())
  const complete = status === 'approved_draft' || status === 'completed'
  const isDoc    = view === 'documentation'
  const multi    = n.blocks.length > 1

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{isDoc ? 'Research Documentation' : 'Your Research Journal'}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            {isDoc
              ? 'Complete system record for this step — mentor review, AI engagement, and ISEF Rule 4.2 disclosure.'
              : 'A curated record of how you turned your question into a testable hypothesis. Print it for your notebook, or copy what\'s meaningful.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ViewToggle value={view} onChange={setView} />
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => handlePrint('journal-step4-print')}>Print</Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <StepReflectionCard projectId={projectId} stepNumber={4} stepLabel={isEng ? 'Step 4: Design Requirements' : 'Step 4: Hypothesis'} onSavedChange={() => { reloadReflection() }} />
      </Box>

      <J.Page id="journal-step4-print">
        <J.Header
          title={isDoc ? 'STEP 4 DOCUMENTATION' : (isEng ? 'ENGINEERING DESIGN JOURNAL' : 'SCIENCE RESEARCH JOURNAL')}
          subtitle={isEng
            ? (isDoc ? 'Complete record — Design Requirements' : 'Step 4 — Design Requirements')
            : (isDoc ? 'Complete record — Hypothesis & Variables' : 'Step 4 — Hypothesis & Variables')}
          date={dateStr}
        />

        {topic && (<><J.Section>MY TOPIC</J.Section><J.Quote>{topic}</J.Quote></>)}

        {isEng && (<>
          {p.problem?.trim() && (<><J.Section>THE PROBLEM</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.problem}</J.Body></>)}
          {(p.for_human_user === false ? p.performance_goal : p.users_needs)?.trim() && (
            <>
              <J.Section>{p.for_human_user === false ? 'PERFORMANCE GOAL' : 'USERS & NEEDS'}</J.Section>
              <J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.for_human_user === false ? p.performance_goal : p.users_needs}</J.Body>
            </>
          )}
          {p.prior_art?.trim() && (<><J.Section>PRIOR ART & GAPS</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.prior_art}</J.Body></>)}
          <J.Section>DESIGN REQUIREMENTS</J.Section>
          {(p.requirements ?? []).map((r, i) => (
            <Box key={i} className="journal-no-break" sx={{ mb: 1 }}>
              <J.Label>Requirement {i + 1}</J.Label>
              {r.requirement?.trim() && <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0.5 }}>{r.requirement}</J.Body>}
              {r.criterion?.trim() && (<><J.Label>Performance criterion</J.Label><J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{r.criterion}</J.Body></>)}
            </Box>
          ))}
          {(p.mvp?.trim() || p.tools?.trim() || p.solution?.trim()) && (
            <>
              <J.Section>SOLUTION</J.Section>
              {p.mvp?.trim() && (<><J.Label>MVP</J.Label><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.mvp}</J.Body></>)}
              {p.tools?.trim() && (<><J.Label>Tools / materials</J.Label><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.tools}</J.Body></>)}
              {p.solution?.trim() && (<><J.Label>Solution</J.Label><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.solution}</J.Body></>)}
            </>
          )}
        </>)}

        {!isEng && (<>
        {n.question?.trim() && (<><J.Section>MY RESEARCH QUESTION</J.Section><J.Body>{n.question}</J.Body></>)}

        <J.Section>MY VARIABLES</J.Section>
        <J.Body sx={{ mb: 1 }}>
          I change my independent variable to see its effect on my dependent variable, keeping other variables constant.
        </J.Body>
        {n.iv?.trim() && (
          <Box className="journal-no-break" sx={{ mb: 1 }}>
            <J.Label>Independent variable (what I change)</J.Label>
            <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{n.iv}</J.Body>
          </Box>
        )}
        {n.cv?.trim() && (
          <Box className="journal-no-break" sx={{ mb: 1 }}>
            <J.Label>Controlled variables (what I keep constant)</J.Label>
            <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{n.cv}</J.Body>
          </Box>
        )}

        {n.blocks.map((b, i) => (
          <Box key={i}>
            <J.Section>{multi ? `HYPOTHESIS ${i + 1}` : 'MY HYPOTHESIS'}</J.Section>
            {b.dv?.trim() && (
              <Box className="journal-no-break" sx={{ mb: 1 }}>
                <J.Label>Dependent variable (what I measure)</J.Label>
                <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{b.dv}</J.Body>
              </Box>
            )}
            {b.dv_kind !== 'derived' && b.dv_measurement?.trim() && (
              <Box className="journal-no-break" sx={{ mb: 1 }}>
                <J.Label>How I measure it</J.Label>
                <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{b.dv_measurement}</J.Body>
              </Box>
            )}
            {b.dv_kind === 'derived' && b.measured_quantities?.trim() && (
              <Box className="journal-no-break" sx={{ mb: 1 }}>
                <J.Label>What I actually measure</J.Label>
                <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{b.measured_quantities}</J.Body>
              </Box>
            )}
            {b.dv_kind === 'derived' && b.formula?.trim() && (
              <Box className="journal-no-break" sx={{ mb: 1 }}>
                <J.Label>How I calculate it</J.Label>
                <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{b.formula}</J.Body>
              </Box>
            )}
            {b.needs_tool && b.tool?.trim() && (
              <Box className="journal-no-break" sx={{ mb: 1 }}>
                <J.Label>Measurement tool I built/configured</J.Label>
                <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{b.tool}</J.Body>
              </Box>
            )}
            {b.hypothesis?.trim() && (
              <>
                <J.Label>Hypothesis</J.Label>
                <J.Body sx={{ fontWeight: 'bold', fontSize: '12pt' }}>{b.hypothesis}</J.Body>
              </>
            )}
            {b.rationale?.trim() && (<><J.Label>Why I expect this</J.Label><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{b.rationale}</J.Body></>)}
            {b.supporting_background?.trim() && (<><J.Label>Supporting background</J.Label><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{b.supporting_background}</J.Body></>)}
          </Box>
        ))}
        </>)}

        <ReflectionBlock refl={reflection} notes={notes} />

        {isDoc && (mentorNote || reviewLog.length > 0) && (
          <>
            <J.Section>MENTOR REVIEW RECORD</J.Section>
            {reviewLog.map((r, i) => (
              <Box key={i} className="journal-no-break" sx={{ mb: 1 }}>
                <J.Label>{r.action === 'approve' ? 'Approved' : 'Sent back with comments'} · {fmtDateTime(r.acted_at)}</J.Label>
                {r.note && <J.Quote>{r.note}</J.Quote>}
              </Box>
            ))}
            {mentorNote && reviewLog.length === 0 && <J.Quote>{mentorNote}</J.Quote>}
          </>
        )}

        {isDoc && <FeedbackRounds rows={feedback.map(r => ({ ...r, field: labelForField(r.field) }))} fieldLabels={{}} />}
        {isDoc && <AiEngagement usage={usage} labels={AI_LABELS} date={dateStr} />}

        {complete && <J.Signatures />}
        {!complete && (
          <J.Body sx={{ mt: 2, fontSize: '10pt', color: '#555', fontStyle: 'italic' }}>
            This entry updates as you refine your work. The final version — with signatures — is ready once your mentor approves this step.
          </J.Body>
        )}
      </J.Page>

      {!topic && !(isEng ? (p.requirements ?? []).some(r => r.requirement?.trim()) : n.blocks.some(b => b.hypothesis?.trim())) && (
        <Alert severity="info" sx={{ mt: 2 }}>Your Step 4 journal fills in as you complete your {isEng ? 'design requirements' : 'hypothesis'}.</Alert>
      )}
    </Box>
  )
}
